import { AxiosResponse } from 'axios';
import api from '../api';
import { getAssetId } from '../teacher';
import {
  IParentHomeworkDetailResponse,
  IParentHomeworkFiltersResponse,
  IParentHomeworkListQueryParams,
  IParentHomeworkListResponse,
  IParentHomeworkSubmitResponse,
  ParentSubmissionFilesByTask,
} from '../../interface/parentHomework.interface';

/**
 * CVA-4730 — a child's own homework endpoints. The base URL already includes
 * `/v2`; all calls are JWT-authed and scoped server-side to the caller's own
 * student record (a child may only ever pass their own `studentId`).
 *
 * Request/response shapes are IDENTICAL to the guardian `v2/parent/homework`
 * endpoints, so the `IParentHomework*` interfaces are reused verbatim rather
 * than duplicated — only the URL prefix and the path-parameter ORDER differ
 * (`/student/homework/:studentId/:homeworkId` vs. the guardian's
 * `/parent/homework/:homeworkId/student/:studentId`). `services/homeworkPortal`
 * is the facade that picks between this service and the guardian one, so
 * nothing above the service layer has to know which audience it is serving.
 */

const fetchStudentHomeworkList = async (
  studentId: string,
  params?: IParentHomeworkListQueryParams,
): Promise<AxiosResponse<IParentHomeworkListResponse>> => {
  const response = await api.get<IParentHomeworkListResponse>(`/student/homework/${studentId}`, {
    params,
  });
  return response;
};

const fetchStudentHomeworkFilters = async (
  studentId: string,
): Promise<AxiosResponse<IParentHomeworkFiltersResponse>> => {
  const response = await api.get<IParentHomeworkFiltersResponse>(
    `/student/homework/${studentId}/filters`,
  );
  return response;
};

const fetchStudentHomeworkDetail = async (
  homeworkId: string,
  studentId: string,
): Promise<AxiosResponse<IParentHomeworkDetailResponse>> => {
  const response = await api.get<IParentHomeworkDetailResponse>(
    `/student/homework/${studentId}/${homeworkId}`,
  );
  return response;
};

/**
 * Submit the child's own work using the SAME upload method as the guardian
 * flow (the backend's documented contract): every file is uploaded first via
 * POST /v2/file (one request per file), then ONE JSON submit references the
 * returned upload_ids per task. The server re-validates size/type against the
 * stored Upload rows and links + stamps everything in a single transaction.
 */
const submitStudentHomework = async (
  homeworkId: string,
  studentId: string,
  filesByTask: ParentSubmissionFilesByTask,
): Promise<AxiosResponse<IParentHomeworkSubmitResponse>> => {
  const tasks: Array<{
    detailId: string;
    files: Array<{ upload_id: string; size?: number }>;
  }> = [];

  // Sequential per-task/per-file uploads (mirrors the guardian service and
  // useHomeworkAttachments) so failures surface with the offending file's name.
  for (const [detailId, files] of Object.entries(filesByTask)) {
    if (!files.length) continue;
    const uploaded: Array<{ upload_id: string; size?: number }> = [];
    for (const file of files) {
      const formData = new FormData();
      formData.append('file', file, file.name);
      const uploadResponse = await getAssetId(formData);
      const uploadId = uploadResponse.data?.[0]?.mediaFiles?.[0]?.id;
      if (!uploadId) {
        throw new Error(`Failed to upload "${file.name}". Please try again.`);
      }
      uploaded.push({ upload_id: uploadId, size: file.size });
    }
    tasks.push({ detailId, files: uploaded });
  }

  const response = await api.post<IParentHomeworkSubmitResponse>(
    `/student/homework/${studentId}/${homeworkId}/submit`,
    { tasks },
  );
  return response;
};

export const studentHomeworkService = {
  fetchStudentHomeworkList,
  fetchStudentHomeworkFilters,
  fetchStudentHomeworkDetail,
  submitStudentHomework,
};
